import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { appealHref } from '../appealLink';
import type { Report as ReportT } from '../types';
import { hoursLabel, money } from '../utils';

const MONEY_BADGE: Record<ReportT['money'][number]['status'], { label: string; cls: string }> = {
  paid: { label: 'Posted', cls: 'ok' },
  collecting: { label: 'Being collected', cls: 'warn' },
  waiting: { label: 'Waiting for you', cls: 'warn' },
};

export function Report({ report, onSeeInLog, onCorrections }: { report: ReportT; onSeeInLog: (kind: string) => void; onCorrections: () => void }) {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem('ew-howcount-seen') !== '1'; } catch { return true; }
  });
  const marked = useRef(false);
  useEffect(() => {
    if (!marked.current && open) {
      marked.current = true;
      try { localStorage.setItem('ew-howcount-seen', '1'); } catch { /* ignore */ }
    }
  }, [open]);

  return (
    <div className="ew-wide">
      <div className="ew-figs">
        <section className="ew-card">
          <div className="ew-fig-label">Staff time ELVA did</div>
          <div className="ew-fig-val num">{report.hours} hours</div>
          <div className="ew-meta">{report.tasks} tasks · <a href="#ew-howcount" className="ew-src" style={{ textDecoration: 'none' }}>How we count</a></div>
        </section>
        <section className="ew-card">
          <div className="ew-fig-label">Money ELVA found</div>
          <div className="ew-fig-val num">{money(report.moneyFound)}</div>
          <div className="ew-meta">{money(report.moneyPaid)} paid so far · {money(report.moneyCollecting)} being collected</div>
        </section>
        <section className="ew-card">
          <div className="ew-fig-label">Who did it</div>
          <div className="ew-fig-val">{report.onItsOwn} on its own · {report.confirmed} confirmed by your team</div>
          <div className="ew-meta">Your team changed {report.changed} and undid {report.undone}</div>
        </section>
      </div>

      <section className="ew-card">
        <h2 className="ew-h2">What ELVA did</h2>
        <div style={{ overflowX: 'auto' }}>
          <table className="ew-tbl">
            <thead><tr><th>Task</th><th className="r">How many</th><th>Counted as</th><th className="r">Time</th><th className="r">Money found</th><th /></tr></thead>
            <tbody>
              {report.rows.map((r) => (
                <tr key={r.kind}>
                  <td>{r.task}{r.subNote ? <div className="ew-meta">{r.subNote}</div> : null}</td>
                  <td className="r num">{r.count}</td>
                  <td>{r.countedAs}</td>
                  <td className="r num">{hoursLabel(r.hours)}</td>
                  <td className="r num">{r.money ? money(r.money) : '—'}</td>
                  <td className="r"><button type="button" className="ew-src" onClick={() => { onSeeInLog(r.kind); }}>See in log</button></td>
                </tr>
              ))}
              <tr className="total">
                <td>Total</td>
                <td className="r num">{report.tasks}</td>
                <td />
                <td className="r num">{hoursLabel(report.rows.reduce((s, r) => s + r.hours, 0))}</td>
                <td className="r num">{money(report.moneyFound)}</td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
        <p className="ew-meta" style={{ marginTop: 10 }}>Money is counted once, on the task that found it. Each denied or short-paid figure is the expected insurer payment; any billed fee shows only as a note.</p>
      </section>

      {report.appeals ? (
        <section className="ew-card" aria-labelledby="ew-appeals">
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '4px 16px', alignItems: 'baseline' }}>
            <h2 className="ew-h2" id="ew-appeals" style={{ margin: 0 }}>Appeals</h2>
            <span className="ew-meta">Everything ELVA did to get denied and short claims paid · <Link to={report.appeals.openHref} className="ew-src">Open Denied &amp; underpaid</Link></span>
          </div>

          <div className="ew-figs" style={{ marginTop: 14 }}>
            {report.appeals.figures.map((f) => (
              <div key={f.label} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px' }}>
                <div className="ew-fig-label">{f.label}</div>
                <div style={{ fontSize: 22, fontWeight: 700, margin: '2px 0', color: f.danger ? 'var(--danger)' : undefined }}>{f.value}</div>
                <div className="ew-meta">{f.meta}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 18, marginTop: 16 }}>
            <div>
              <span className="ew-eyebrow">What ELVA did for them</span>
              {report.appeals.did.map((d, i) => (
                <div key={`did-${String(i)}`} className="ew-tick"><span className="ew-tickicon" aria-hidden="true">✓</span><span>{d.text}{d.link ? <> <Link to={d.link.href} className="ew-src">{d.link.label}</Link></> : null}</span></div>
              ))}
            </div>
            <div>
              <span className="ew-eyebrow">How they went out</span>
              {report.appeals.channels.map((c) => (
                <div key={c.label} style={{ display: 'flex', gap: 12, justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderTop: '1px solid var(--border)' }}>
                  <span><strong>{c.label}</strong><div className="ew-meta">{c.detail}</div></span>
                  <span className={`ew-badge ${c.badgeCls}`}>{c.badge}</span>
                </div>
              ))}
              <p className="ew-meta" style={{ marginTop: 10 }}>{report.appeals.channelFooter}</p>
            </div>
          </div>

          {report.appeals.decision ? (
            <p className="ew-meta" style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>{report.appeals.decision.text} <Link to={appealHref(report.appeals.decision.link.href) ?? report.appeals.openHref} className="ew-src">{report.appeals.decision.link.label}</Link></p>
          ) : null}
        </section>
      ) : null}

      <section className="ew-card">
        <h2 className="ew-h2">The money ELVA found</h2>
        {report.money.map((m) => {
          const b = MONEY_BADGE[m.status];
          return (
            <div key={m.what} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', padding: '12px 0', borderTop: '1px solid var(--border)' }}>
              <div style={{ flex: '1 1 auto' }}>
                <div style={{ fontWeight: 600 }}>{m.what}</div>
                <div className="ew-meta">{m.note}{m.link ? <> · <Link to={m.link.href} className="ew-src">{m.link.label}</Link></> : null}</div>
              </div>
              <div className="num" style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{money(m.amount)}</div>
              <span className={`ew-badge ${b.cls}`}>{b.label}</span>
            </div>
          );
        })}
      </section>

      <section className="ew-card">
        <h2 className="ew-h2">What your team corrected</h2>
        <p style={{ margin: '0 0 10px', color: 'var(--fg2)' }}>Your team changed {report.changed} of the {report.confirmed} things it confirmed, and undid {report.undone}. ELVA learned from each one.</p>
        {report.corrections.map((c) => (
          <div key={c.id} className="ew-tick" style={{ borderTop: '1px solid var(--border)', paddingTop: 10 }}>
            <span className="ew-badge mute">{c.label}</span>
            <div>{c.text}</div>
          </div>
        ))}
        <p style={{ marginTop: 10 }}><button type="button" className="ew-src" onClick={onCorrections}>See all {report.changed + report.undone} in the log</button></p>
      </section>

      <details id="ew-howcount" className="ew-details" open={open} onToggle={(e) => { setOpen((e.target as HTMLDetailsElement).open); }}>
        <summary>How we count</summary>
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 10, color: 'var(--fg2)' }}>
          <p style={{ margin: 0 }}>Calls count their real length, hold time included, because that’s the time someone on your team would have spent on the phone.</p>
          <p style={{ margin: 0 }}>Appeals count 45 minutes each: reading the denial, gathering the documents, writing the letter, sending it and checking the payer got it. Calls about appeals count their real length, under Calls.</p>
          <p style={{ margin: 0 }}>Everything else counts the minutes a person usually spends on that task, shown next to each task above. Owners and admins set them in <Link to="/settings/how-counted">Settings › How ELVA’s work is counted</Link>. A change counts from that day; past months aren’t recounted.</p>
          <p style={{ margin: 0 }}>Money found is money that wasn’t on its way before ELVA acted: denied or underpaid claims, payments nobody posted, payments that went missing. It is counted as paid only once the money arrives.</p>
        </div>
      </details>
    </div>
  );
}
